import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchEquityFlow, saveEquityProjectSplit } from "../api";
import {
  Card, Loading, ErrorState, EmptyState,
  useHorizontalScrollShadow, useColumnWidths, ColResize,
} from "../components/ui";
import { money0, num } from "../lib/format";
import { readUi, writeUi } from "../lib/persist";
import EquityAudit from "../components/EquityAudit";

/**
 * Equity.
 *
 * A partner's claim is built in four steps, and each one is a column on the
 * page so the argument can be followed rather than taken on trust:
 *
 *   1. RETAINED PROFIT. Half the gross profit on the work they ran. The other
 *      half was never available to keep, and what was available is assumed to
 *      have been taken - so what a partner has actually drawn to date does not
 *      move this figure, and nobody is rewarded for leaving their share in.
 *
 *   2. THE HANDOVER SPLIT. Work a partner ran but did not bring in is split
 *      with whoever did: the runner takes their share, the originator the
 *      rest. The share is prefilled from the runner's genesis ratio - run
 *      little of your own book and you keep little of someone else's - and can
 *      be overridden for a single project. Where the originator is not a
 *      partner the remainder is set aside under Others, so the two sides
 *      always add to a pound.
 *
 *   3. THE SPLIT. Each partner's credit as a share of the total.
 *
 *   4. ENTERPRISE VALUE. Retained profit is NOT equity. The split is applied to
 *      the enterprise value to give each partner a figure in pounds, and that
 *      is what a drawdown comes out of. Draw your whole slice and you are left
 *      with nothing.
 *
 * The measures are plain totals over the years ticked on. Years are either in
 * or out: the model no longer strikes a share inside each year, so there is
 * nothing for a fractional weight to act on.
 */

/** Share of gross profit treated as retained. */
const DEFAULT_RETAINED = 0.5;
/**
 * A slice run for someone who is not a partner - CC, OLD, EB. The runner takes
 * 90p and the last 10p is set aside under `OTHERS`, so every pound of profit
 * lands somewhere instead of the originator's share quietly vanishing.
 */
const DEFAULT_CC_SHARE = 90;
const OTHERS = "__others";
/**
 * Running work you did not bring in earns your genesis ratio, capped at 50p:
 * half and half by default, less for a partner who brings in little of their
 * own work, never more. Without the cap a strong originator would be paid for
 * running someone else's work while the partner who actually brought it in
 * kept a few pence, which is the opposite of what the measure is for.
 */
const DEFAULT_THRESHOLD = 50;
/** Placeholder only - the real number is whatever the partners agree. */
const DEFAULT_EV = 5_000_000;

const YEARS = ["2022", "2023", "2024", "2025", "2026"];
const DEFAULT_YEAR_W = { 2022: 100, 2023: 100, 2024: 100, 2025: 100, 2026: 0 };

/** Not partners: the admin login and the central overhead pool. */
const NOT_PARTNERS = ["fd", "oh"];

/** Eligible but held back until someone decides - they start unticked. */
const DEFAULT_OFF = ["gj", "rp"];

/**
 * What a partner's OWN work earns, in pence per £1. Work that changed hands is
 * not priced here - it is split slice by slice further down, because the two
 * sides of a handover are now a decision about that project rather than a rate
 * that applies to everybody.
 */
const RATES = [
  { key: "ownGp", field: "go", label: "Genesis Profit", compact: "Genesis profit" },
];
const DEFAULT_RATES = { ownGp: 100 };

/** Stored rates plus the derived one, which is what the maths actually uses. */
const effective = (rates) => {
  const out = { ...rates };
  for (const c of RATES) if (c.derived) out[c.key] = c.derived(rates);
  return out;
};

const pctStr = (v, dp = 2) => `${(v * 100).toFixed(dp)}%`;

/** Compact money for the narrow columns: £18.79m / £733.76k. */
const moneyShort = (value) => {
  const n = num(value);
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `${sign}£${(abs / 1_000).toFixed(2)}k`;
  return money0(n);
};

/**
 * One row of the settings table: what it is, the box, and the unit. Every
 * control on the page is one of these, so they line up as a single column of
 * numbers that can be read down rather than hunted for.
 */
/** One control on the top bar: label, box, unit, all on the line. */
const Inline = ({ label, value, onChange, suffix, max, money = false }) => (
  <label className="eq-ctl">
    <span className="eq-ctl-label">{label}</span>
    {money ? (
      <input type="text" inputMode="numeric" className="eq-num" aria-label={label}
             value={value.toLocaleString("en-GB")}
             onChange={(e) => {
               const n = Number(e.target.value.replace(/[^0-9]/g, ""));
               if (Number.isFinite(n)) onChange(Math.max(0, n));
             }} />
    ) : (
      <input type="number" className="eq-num" value={value} min="0" max={max} step="1"
             aria-label={label}
             onChange={(e) => {
               const n = Number(e.target.value);
               if (Number.isFinite(n)) onChange(Math.max(0, Math.min(max, n)));
             }} />
    )}
    {suffix && <span className="eq-ctl-unit">{suffix}</span>}
  </label>
);


export default function Equity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const scrollRef = useHorizontalScrollShadow();

  const [yearW, setYearW] = useState(() => {
    const saved = readUi("eq.yearW", DEFAULT_YEAR_W);
    return Object.fromEntries(YEARS.map((year) => [year, saved?.[year] ? 100 : 0]));
  });
  const [rates, setRates] = useState(() => readUi("eq.rates5", DEFAULT_RATES));
  /* Fixed at half, and no longer a control on the page: the other half of
     profit was never available to keep. Change the constant to move it. */
  const retained = DEFAULT_RETAINED;
  /* Saved in the database, not the browser: these are decisions about how the
     firm splits money, not one person's what-if. */
  const [splits, setSplits] = useState({});
  const [partnerRate, setPartnerRate] = useState({});
  const [saving, setSaving] = useState("");
  const [ev, setEv] = useState(() => readUi("eq.ev", DEFAULT_EV));
  const [ccShare, setCcShare] = useState(() => readUi("eq.cc", DEFAULT_CC_SHARE));
  const [threshold, setThreshold] = useState(() => readUi("eq.threshold", DEFAULT_THRESHOLD));
  const [included, setIncluded] = useState(() => readUi("eq.included", null));
  /* Scenario only - deliberately not persisted. A what-if should not still be
     sitting there tomorrow pretending to be the position. */
  const [newDraw, setNewDraw] = useState({});
  const [view, setView] = useState(() => readUi("eq.view", "split"));

  /* Both tables are wide and every reader wants a different column wide, so
     both are draggable and remember their widths in this browser. */
  const splitCols = useMemo(() => [
    { id: "tick", width: 40 }, { id: "partner", width: 150 },
    { id: "revRan", width: 82 }, { id: "gp", width: 82 }, { id: "margin", width: 66 },
    { id: "own", width: 132 }, { id: "ranFor", width: 132 },
    { id: "outside", width: 132 }, { id: "handed", width: 132 },
    { id: "credit", width: 92 }, { id: "equity", width: 76 }, { id: "value", width: 84 },
    { id: "draw", width: 130 }, { id: "change", width: 104 },
  ], []);
  const split = useColumnWidths("eq.cols.split", splitCols);

  const handCols = useMemo(() => [
    { id: "project", width: 170 }, { id: "client", width: 150 }, { id: "year", width: 54 },
    { id: "ran", width: 130 }, { id: "orig", width: 130 }, { id: "kept", width: 100 },
    { id: "share", width: 78 }, { id: "origShare", width: 82 },
    { id: "toRunner", width: 96 }, { id: "toOrig", width: 100 }, { id: "from", width: 118 },
  ], []);
  const hand = useColumnWidths("eq.cols.hand", handCols);
  /* Table controls for the project splits - 452 handovers is too many to scan,
     so the table opens on the biggest and is searched, not scrolled. */
  const [query, setQuery] = useState("");
  const [onlySet, setOnlySet] = useState(false);
  /* Biggest retained profit first: the rows worth arguing about are the ones
     with the most money on them. */
  const [sort, setSort] = useState({ key: "kept", dir: "desc" });

  const load = useCallback(() => {
    setLoading(true); setError("");
    fetchEquityFlow().then((d) => {
      setData(d);
      setSplits(Object.fromEntries((d.splits || []).map(
        (r) => [`${r.project}|${r.ran}|${r.orig}|${r.year}`, r.runner_share])));
      setPartnerRate(d.partnerRates || {});
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { writeUi("eq.yearW", yearW); }, [yearW]);
  useEffect(() => { writeUi("eq.rates5", rates); }, [rates]);
  useEffect(() => { writeUi("eq.view", view); }, [view]);
  useEffect(() => { writeUi("eq.ev", ev); }, [ev]);
  useEffect(() => { writeUi("eq.cc", ccShare); }, [ccShare]);
  useEffect(() => { writeUi("eq.threshold", threshold); }, [threshold]);
  useEffect(() => { if (included) writeUi("eq.included", included); }, [included]);

  const eff = useMemo(() => effective(rates), [rates]);

  /** Project number -> its name and customer, for the handover table. */
  const projectMeta = useCallback(
    (project) => data?.projects?.[project] || {}, [data]);

  const label = useCallback(
    (u) => (u === OTHERS ? "Others (outside genesis)"
            : data?.names?.[u] ? `${data.names[u]} (${u.toUpperCase()})` : u.toUpperCase()),
    [data]
  );

  /** The years ticked on - what every money figure on the page covers. */
  const inScope = useMemo(() => YEARS.filter((y) => (yearW[y] || 0) > 0), [yearW]);
  const yearsInScope = useMemo(() => {
    if (!inScope.length) return "no years";
    return inScope.length === 1 ? inScope[0] : `${inScope[0]}–${inScope[inScope.length - 1]}`;
  }, [inScope]);

  /** Everyone who could be in the split, with the years they have on record. */
  const shell = useMemo(() => {
    const g = new Map();
    for (const r of data?.rows || []) {
      if (!YEARS.includes(r.year) || NOT_PARTNERS.includes(r.partner)) continue;
      const p = g.get(r.partner) || { partner: r.partner, years: {} };
      p.years[r.year] = { ro: num(r.rev_own), rg: num(r.rev_given), drawn: num(r.drawn) };
      g.set(r.partner, p);
    }
    return [...g.values()];
  }, [data]);

  const slices = useMemo(
    () => (data?.matrix || []).filter((m) => YEARS.includes(m.year))
      .map((m) => ({ ran: m.ran, orig: m.orig, year: m.year, project: m.project,
                     rev: num(m.rev), gp: num(m.gp) })),
    [data]
  );

  /**
   * The four measures every later column is built from, totalled over the
   * years in scope. `revOwn` is revenue a partner both brought in AND ran, so
   * it is the numerator of the genesis test; `revOrig` - everything they
   * brought in, however it was delivered - is its denominator.
   */
  const measured = useMemo(() => {
    const t = {};
    const at = (u) => (t[u] = t[u] || {
      revRan: 0, revOwn: 0, revOrig: 0, gpRan: 0, go: 0, gg: 0, gf: 0,
    });
    for (const p of shell) at(p.partner);
    for (const s of slices) {
      if (!inScope.includes(s.year)) continue;
      const kept = s.gp * retained;
      if (t[s.ran]) { at(s.ran).revRan += s.rev; at(s.ran).gpRan += s.gp; }
      if (t[s.orig]) at(s.orig).revOrig += s.rev;
      if (s.ran === s.orig) {
        if (t[s.ran]) { at(s.ran).revOwn += s.rev; at(s.ran).go += kept; }
      } else {
        if (t[s.ran]) at(s.ran).gf += kept;
        if (t[s.orig]) at(s.orig).gg += kept;
      }
    }
    return t;
  }, [shell, slices, inScope, retained]);

  const eligible = shell;
  const eligibleSet = useMemo(
    () => new Set(eligible.map((p) => p.partner)), [eligible]);
  const ticked = useMemo(
    () => (included === null
      ? eligible.map((p) => p.partner).filter((u) => !DEFAULT_OFF.includes(u))
      : included),
    [included, eligible]
  );

  /**
   * What the RUNNER takes on one slice, in pence. Three layers, most specific
   * first: a saved decision about this project, then this partner's own rate,
   * then the prefill - their genesis ratio against another partner, or a flat
   * 90 against an outsider, where there is no partner on the other side to
   * reward.
   */
  const shareFor = useCallback((s2, genesisOf) => {
    const saved = splits[`${s2.project}|${s2.ran}|${s2.orig}|${s2.year}`];
    if (saved !== undefined && saved !== null) return { share: saved, from: "project" };
    /* A runner who is not a partner - fd, the overhead pool - cannot hold a
       share, so the whole retained half goes to whoever brought the work in.
       Without this the runner's part would be credited to nobody and quietly
       leave the pool. */
    if (!eligibleSet.has(s2.ran)) return { share: 0, from: "notpartner" };
    const rate = partnerRate[s2.ran];
    if (rate !== undefined && rate !== null) return { share: rate, from: "partner" };
    if (!eligibleSet.has(s2.orig)) return { share: ccShare, from: "outside" };
    return { share: Math.min((genesisOf[s2.ran] ?? 1) * 100, threshold), from: "genesis" };
  }, [splits, partnerRate, eligibleSet, ccShare, threshold]);

  /**
   * The whole model.
   *
   *   1. Half the gross profit on a slice is retained - the rest was never
   *      available to share.
   *   2. Ran it AND brought it in: the whole of that retained half, priced by
   *      the two rates above.
   *   3. Changed hands: the runner takes their share, the originator the rest.
   *      Where the originator is not a partner their part goes to Others, so
   *      no pound is created or lost by the split.
   *   4. The pool is divided, applied to enterprise value, and a drawdown
   *      comes out of a partner's slice of that.
   *
   * The genesis ratio enters ONCE, as the prefill in step 3. There is no
   * second pass scaling a partner's total: that would dock their own book for
   * a fact about work they did not bring in.
   */
  const scored = useMemo(() => {
    const live = eligible.filter((p) => ticked.includes(p.partner));
    const liveSet = new Set(live.map((p) => p.partner));
    const genesisOf = {};
    for (const p of live) {
      const m = measured[p.partner];
      genesisOf[p.partner] = m && m.revRan > 0 ? m.revOwn / m.revRan : 1;
    }
    const credit = {}; const own = {}; const handed = {}; const ranFor = {};
    /* Work whose originator is nobody - CC, OLD, EB. Kept apart from partner
       handovers because it is priced differently, and blending the two into
       one rate hides which is which. */
    const outside = {};
    /* The whole retained half of each bucket, before any share is applied.
       Dividing the credit by it gives the rate a partner ACTUALLY got, which
       is what the page shows - a single prefill would be a lie once any
       project has been overridden. */
    const ownBase = {}; const handedBase = {}; const ranForBase = {};
    const outsideBase = {};
    const bump = (u, v, bucket) => {
      if (!u) return;
      credit[u] = (credit[u] || 0) + v;
      bucket[u] = (bucket[u] || 0) + v;
    };
    const base = (u, v, bucket) => { if (u) bucket[u] = (bucket[u] || 0) + v; };
    for (const s2 of slices) {
      if (!inScope.includes(s2.year)) continue;
      const kept = s2.gp * retained;
      if (s2.ran === s2.orig) {
        if (liveSet.has(s2.ran)) {
          bump(s2.ran, kept * ((eff.ownGp || 0) / 100), own);
          base(s2.ran, kept, ownBase);
        }
        continue;
      }
      const { share } = shareFor(s2, genesisOf);
      const fromOutside = !eligibleSet.has(s2.orig);
      if (liveSet.has(s2.ran)) {
        bump(s2.ran, kept * (share / 100), fromOutside ? outside : ranFor);
        base(s2.ran, kept, fromOutside ? outsideBase : ranForBase);
      }
      const rest = kept * (1 - share / 100);
      if (liveSet.has(s2.orig)) { bump(s2.orig, rest, handed); base(s2.orig, kept, handedBase); }
      else if (!eligibleSet.has(s2.orig)) bump(OTHERS, rest, handed);
      /* An originator who IS an eligible partner but is unticked keeps their
         share out of the pool deliberately - that is what unticking means. */
    }
    const rows = live.map((p) => {
      const m = measured[p.partner] || { revRan: 0, revOwn: 0, revOrig: 0, gpRan: 0 };
      const drawn = inScope.reduce((t, y) => t + (p.years[y]?.drawn || 0), 0);
      const u = p.partner;
      const rate = (got, all) => (all ? (got || 0) / all : null);
      return { ...p, ...m, genesis: genesisOf[u],
               own: own[u] || 0, handed: handed[u] || 0, ranFor: ranFor[u] || 0,
               outside: outside[u] || 0,
               ownPct: rate(own[u], ownBase[u]),
               ranForPct: rate(ranFor[u], ranForBase[u]),
               outsidePct: rate(outside[u], outsideBase[u]),
               handedPct: rate(handed[u], handedBase[u]),
               contribution: credit[u] || 0, drawn };
    });
    if (credit[OTHERS]) {
      rows.push({ partner: OTHERS, years: {}, revRan: 0, revOwn: 0, revOrig: 0, gpRan: 0,
                  genesis: null, own: 0, handed: handed[OTHERS] || 0, ranFor: 0,
                  outside: 0,
                  ownPct: null, ranForPct: null, outsidePct: null, handedPct: null,
                  contribution: credit[OTHERS], drawn: 0 });
    }
    const pool = rows.reduce((t, r) => t + r.contribution, 0);
    const withEquity = rows.map((r) => {
      const equity = pool ? (r.contribution / pool) * 100 : 0;
      const value = (equity / 100) * ev;
      return { ...r, adjusted: r.contribution, equity, value,
               after: Math.max(0, value - (newDraw[r.partner] || 0)) };
    });
    const left = withEquity.reduce((t, r) => t + r.after, 0);
    return withEquity
      .map((r) => ({ ...r, equityAfter: left ? (r.after / left) * 100 : 0 }))
      .sort((a, b) => b.equity - a.equity);
  }, [eligible, ticked, measured, slices, inScope, retained, eff, ev, newDraw,
      shareFor, eligibleSet]);

  /**
   * Saving. The screen updates first and the write follows, because a split is
   * a judgement someone is making across a long table and a round trip per
   * keystroke would make it unusable. A failed write says so and puts the old
   * value back rather than leaving a number on screen that is not in the
   * database.
   */
  const setSplit = (slice, raw) => {
    const key = `${slice.project}|${slice.ran}|${slice.orig}|${slice.year}`;
    const was = splits[key];
    const share = raw === "" ? null : Math.max(0, Math.min(100, Number(raw)));
    if (share !== null && !Number.isFinite(share)) return;
    setSplits((prev) => {
      const next = { ...prev };
      if (share === null) delete next[key]; else next[key] = share;
      return next;
    });
    saveEquityProjectSplit({ project: slice.project, ran: slice.ran, orig: slice.orig,
                             year: slice.year, runner_share: share })
      .then(() => setSaving(""))
      .catch((e) => {
        setSaving(`Could not save ${slice.project}: ${e.message}`);
        setSplits((prev) => {
          const next = { ...prev };
          if (was === undefined) delete next[key]; else next[key] = was;
          return next;
        });
      });
  };

  const anyDraw = Object.values(newDraw).some((v) => v > 0);
  const setDraw = (u, raw) => {
    const n = Math.max(0, Math.round(Number(String(raw).replace(/[^0-9.-]/g, "")) || 0));
    setNewDraw({ ...newDraw, [u]: n });
  };

  /**
   * The same split struck on one year at a time, genesis test and all. It is
   * NOT what the headline is built from - that is a plain total over the years
   * in scope - but it shows which years carry a partner's claim.
   */
  const perYear = useMemo(() => {
    const out = {};
    const genesisOf = {};
    for (const p of scored) genesisOf[p.partner] = p.genesis ?? 1;
    for (const year of YEARS) {
      const t = {};
      for (const s2 of slices) {
        if (s2.year !== year) continue;
        const kept = s2.gp * retained;
        const add = (u, v) => { if (u) t[u] = (t[u] || 0) + v; };
        if (s2.ran === s2.orig) {
          if (eligibleSet.has(s2.ran)) {
            add(s2.ran, kept * ((eff.ownGp || 0) / 100));
          }
          continue;
        }
        const { share } = shareFor(s2, genesisOf);
        if (eligibleSet.has(s2.ran)) add(s2.ran, kept * (share / 100));
        const rest = kept * (1 - share / 100);
        if (eligibleSet.has(s2.orig)) add(s2.orig, rest);
        else add(OTHERS, rest);
      }
      const sum = Object.values(t).reduce((a, b) => a + b, 0);
      out[year] = Object.fromEntries(
        Object.entries(t).map(([u, v]) => [u, sum ? (v / sum) * 100 : 0]));
    }
    return out;
  }, [scored, slices, retained, eff, shareFor, eligibleSet]);

  /**
   * Every slice that changed hands, biggest profit first, with its split
   * resolved and labelled by where the number came from. Only handovers: a
   * slice a partner both brought in and ran has no second party to split with.
   */
  const handovers = useMemo(() => {
    const genesisOf = {};
    for (const p of scored) genesisOf[p.partner] = p.genesis ?? 1;
    return slices
      .filter((s2) => s2.ran !== s2.orig && inScope.includes(s2.year))
      .map((s2) => {
        const { share, from } = shareFor(s2, genesisOf);
        const kept = s2.gp * retained;
        const meta = projectMeta(s2.project);
        return { ...s2, kept, share, from,
                 name: meta.name || null, client: meta.client || null,
                 key: `${s2.project}|${s2.ran}|${s2.orig}|${s2.year}`,
                 toRunner: kept * (share / 100), toOrig: kept * (1 - share / 100) };
      })
      .sort((a, b) => Math.abs(b.kept) - Math.abs(a.kept));
  }, [slices, inScope, shareFor, scored, retained, projectMeta]);

  /** Money and rates sort by size, everything else alphabetically. */
  const sortBy = useCallback((list) => {
    const { key, dir } = sort;
    const num = new Set(["year", "kept", "share", "toRunner", "toOrig"]);
    const at = (h) => {
      if (key === "orig") return eligibleSet.has(h.orig) ? label(h.orig) : h.orig;
      if (key === "ran") return label(h.ran);
      if (key === "project") return h.name || h.project;
      if (key === "client") return h.client || "";
      return h[key];
    };
    const out = [...list].sort((a, b) => {
      const x = at(a); const y = at(b);
      if (num.has(key)) return Number(x) - Number(y);
      return String(x).localeCompare(String(y), "en-GB", { numeric: true });
    });
    return dir === "desc" ? out.reverse() : out;
  }, [sort, label, eligibleSet]);

  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const shownHandovers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hit = q
      ? handovers.filter((h) => h.project.toLowerCase().includes(q)
          || (h.name || "").toLowerCase().includes(q)
          || (h.client || "").toLowerCase().includes(q)
          || h.ran.toLowerCase().includes(q) || h.orig.toLowerCase().includes(q)
          || (data?.names?.[h.ran] || "").toLowerCase().includes(q)
          || (data?.names?.[h.orig] || "").toLowerCase().includes(q))
      : handovers;
    return sortBy(onlySet ? hit.filter((h) => h.from === "project") : hit);
  }, [handovers, query, onlySet, data, sortBy]);

  const notTicked = eligible.filter((p) => !ticked.includes(p.partner));
  const toggle = (u) =>
    setIncluded(ticked.includes(u) ? ticked.filter((v) => v !== u) : [...ticked, u]);

  const totals = useMemo(() => ({
    revRan: scored.reduce((s, p) => s + p.revRan, 0),
    gpRan: scored.reduce((s, p) => s + p.gpRan, 0),
    contribution: scored.reduce((s, p) => s + p.contribution, 0),
    adjusted: scored.reduce((s, p) => s + p.adjusted, 0),
  }), [scored]);

  if (loading) return <main className="page"><Loading /></main>;
  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;

  return (
    <main className="page page--wide equity-page">
      <div className="eq-topbar">
        <div className="eq-tabs" role="tablist" aria-label="Equity views">
          {[["split", "Split"], ["audit", "Audit"]].map(([k, t]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k}
                    className={`eq-tab${view === k ? " is-on" : ""}`}
                    onClick={() => setView(k)}>{t}</button>
          ))}
        </div>
        <span className="eq-topsub">{scored.length} partners · {yearsInScope}</span>
        {RATES.map((c) => (
          <Inline key={c.key} label={c.label} suffix="p" max={100} value={eff[c.key] || 0}
                  onChange={(v) => setRates({ ...rates, [c.key]: v })} />
        ))}
        <Inline label="CC / OLD" suffix="p" max={100} value={ccShare} onChange={setCcShare} />
        <Inline label="Threshold" suffix="%" max={100} value={threshold} onChange={setThreshold} />
        <Inline label="Enterprise value" suffix="£" money value={ev} onChange={setEv} />
        <div className="eq-year-ticks" role="group" aria-label="Years included in equity">
          {YEARS.map((year) => (
            <label key={year} className={`eq-year-tick${yearW[year] ? " is-on" : ""}`}>
              <input type="checkbox" checked={Boolean(yearW[year])}
                     onChange={(e) => setYearW({ ...yearW, [year]: e.target.checked ? 100 : 0 })} />
              <span>{year}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="stack">
        {view === "audit" ? (
          <EquityAudit label={label} partners={eligible.map((p) => p.partner).sort()} />
        ) : (
        <>
        {!scored.length ? (
          <Card><EmptyState title="Nobody to split between" hint="Every partner is excluded." /></Card>
        ) : (
          <Card title="Proposed split"
                note={`click a row to include or exclude that partner · ${yearsInScope}`} flush>
            <div className="tbl-wrap tbl-wrap--flush eq-tall" ref={scrollRef}>
              <table className="tbl tbl-zebra eq-split-tbl eq-sticky-head eq-fixed">
                <colgroup>
                  {splitCols.map((c) => (
                    <col key={c.id} style={{ width: split.widths[c.id] }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th />
                    <th className="sticky-col eq-name">Partner<ColResize id="partner" onMouseDown={split.onMouseDown} /></th>
                    <th className="num eq-col-tight" title="Revenue on every job they delivered">
                      <span className="eq-th-line">Revenue</span>
                      <span className="eq-th-line">ran</span>
                    <ColResize id="revRan" onMouseDown={split.onMouseDown} /></th>
                    <th className="num eq-col-tight" title="Gross profit on that same work">
                      <span className="eq-th-line">Gross</span>
                      <span className="eq-th-line">profit</span>
                    <ColResize id="gp" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="Gross profit ÷ revenue">Margin<ColResize id="margin" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="Brought it in and ran it — the whole retained half">
                      <span className="eq-th-line">Pure</span>
                      <span className="eq-th-line">genesis</span>
                    <ColResize id="own" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="Another PARTNER's work that they ran — never more than 50p">
                      <span className="eq-th-line">Non-genesis</span>
                      <span className="eq-th-line">they ran</span>
                    <ColResize id="ranFor" onMouseDown={split.onMouseDown} /></th>
                    <th className="num"
                        title="Work with no partner behind it — CC, OLD, EB — that they ran">
                      <span className="eq-th-line">CC / OLD</span>
                      <span className="eq-th-line">they ran</span>
                    <ColResize id="outside" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="Their own work that someone else ran — what comes back to them">
                      <span className="eq-th-line">Their genesis</span>
                      <span className="eq-th-line">run by others</span>
                    <ColResize id="handed" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="The three added together — what the split is struck on">
                      Credit
                    <ColResize id="credit" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" style={{ width: 100 }}>Equity<ColResize id="equity" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="Their share of enterprise value">Value<ColResize id="value" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="Model a drawdown out of that value">New drawdown<ColResize id="draw" onMouseDown={split.onMouseDown} /></th>
                    <th className="num" title="What the drawdowns do to this partner's share">
                      Change
                    <ColResize id="change" onMouseDown={split.onMouseDown} /></th>
                  </tr>
                </thead>
                <tbody>
                  {scored.map((p) => {
                    const delta = p.equityAfter - p.equity;
                    return (
                    <tr key={p.partner} className="eq-row" onClick={() => toggle(p.partner)}>
                      <td><input type="checkbox" checked readOnly aria-label={`Include ${p.partner}`} /></td>
                      <td className="sticky-col eq-name">{label(p.partner)}</td>
                      <td className="num">{moneyShort(p.revRan)}</td>
                      <td className="num">{moneyShort(p.gpRan)}</td>
                      <td className="num muted">{pctStr(p.revRan ? p.gpRan / p.revRan : 0, 1)}</td>
                      {[["own", "ownPct"], ["ranFor", "ranForPct"],
                        ["outside", "outsidePct"], ["handed", "handedPct"]]
                        .map(([f, pf]) => (
                          <td key={f} className={`num ${p[f] < 0 ? "neg" : ""}`}>
                            {money0(p[f])}
                            {p[pf] !== null && p[pf] !== undefined && (
                              <span className="eq-inline-pct">
                                ({(p[pf] * 100).toFixed(1)}%)
                              </span>
                            )}
                          </td>
                        ))}
                      <td className="num">{money0(p.contribution)}</td>
                      <td className="num" style={{ fontWeight: 700 }}>{p.equity.toFixed(2)}%</td>
                      <td className="num">{money0(p.value)}</td>
                      {/* The row toggles a partner in and out, so the box has to
                          swallow its own clicks or typing a figure would drop
                          that partner from the split. */}
                      <td className="num" onClick={(e) => e.stopPropagation()}>
                        <input type="number" className="eq-num eq-draw" min="0" step="1000"
                               aria-label={`New drawdown for ${p.partner}`}
                               value={newDraw[p.partner] || ""} placeholder="0"
                               onChange={(e) => setDraw(p.partner, e.target.value)} />
                      </td>
                      <td className={`num ${delta < -0.005 ? "neg" : delta > 0.005 ? "pos" : "muted"}`}>
                        {!anyDraw || Math.abs(delta) < 0.005
                          ? "—"
                          : <>
                              {`${delta > 0 ? "+" : ""}${delta.toFixed(2)}`}
                              <span className="eq-inline-pct">
                                ({p.equityAfter.toFixed(2)}%)
                              </span>
                            </>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td className="sticky-col eq-name">Total</td>
                    <td className="num">{moneyShort(totals.revRan)}</td>
                    <td className="num">{moneyShort(totals.gpRan)}</td>
                    <td className="num muted">
                      {pctStr(totals.revRan ? totals.gpRan / totals.revRan : 0, 1)}
                    </td>
                    {["own", "ranFor", "outside", "handed"].map((f) => (
                      <td key={f} className="num">
                        {money0(scored.reduce((t, p) => t + (p[f] || 0), 0))}
                      </td>
                    ))}
                    <td className="num">{money0(totals.contribution)}</td>
                    <td className="num" style={{ fontWeight: 700 }}>
                      {scored.reduce((s, p) => s + p.equity, 0).toFixed(2)}%
                    </td>
                    <td className="num">{money0(ev)}</td>
                    <td className="num">
                      {money0(Object.values(newDraw).reduce((t, v) => t + (v || 0), 0))}
                    </td>
                    <td className="num">
                      {anyDraw && (
                        <button type="button" className="btn btn--ghost btn--sm"
                                onClick={() => setNewDraw({})}>Clear</button>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        )}

        {!!handovers.length && (
          <Card title="Work that changed hands"
                note={`${shownHandovers.length} of ${handovers.length} · ${yearsInScope}`}
                flush
                actions={(
                  <>
                    {saving && <span className="eq-saving neg">{saving}</span>}
                    <input type="search" className="eq-search" value={query}
                           placeholder="Search project or partner"
                           aria-label="Search handovers"
                           onChange={(e) => setQuery(e.target.value)} />
                    <label className="eq-inline-check">
                      <input type="checkbox" checked={onlySet}
                             onChange={(e) => setOnlySet(e.target.checked)} />
                      <span>Only ones I have set</span>
                    </label>
                  </>
                )}>
            <div className="tbl-wrap tbl-wrap--flush eq-tall">
              <table className="tbl tbl-zebra eq-split-tbl eq-sticky-head eq-fixed">
                <colgroup>
                  {handCols.map((c) => (
                    <col key={c.id} style={{ width: hand.widths[c.id] }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th><button type="button" className="eq-sortbtn" onClick={() => toggleSort("project")}>Project</button>{sort.key === "project" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="project" onMouseDown={hand.onMouseDown} /></th>
                    <th><button type="button" className="eq-sortbtn" onClick={() => toggleSort("client")}>Customer</button>{sort.key === "client" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="client" onMouseDown={hand.onMouseDown} /></th>
                    <th className="num"><button type="button" className="eq-sortbtn" onClick={() => toggleSort("year")}>Year</button>{sort.key === "year" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="year" onMouseDown={hand.onMouseDown} /></th>
                    <th><button type="button" className="eq-sortbtn" onClick={() => toggleSort("ran")}>Ran by</button>{sort.key === "ran" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="ran" onMouseDown={hand.onMouseDown} /></th>
                    <th><button type="button" className="eq-sortbtn" onClick={() => toggleSort("orig")}>Brought in by</button>{sort.key === "orig" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="orig" onMouseDown={hand.onMouseDown} /></th>
                    <th className="num"><button type="button" className="eq-sortbtn" onClick={() => toggleSort("kept")}>Retained profit</button>{sort.key === "kept" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="kept" onMouseDown={hand.onMouseDown} /></th>
                    <th className="num" title="Pence in the pound to the runner. Blank follows the rule."><button type="button" className="eq-sortbtn" onClick={() => toggleSort("share")}>
                      <span className="eq-th-line">Runner</span>
                      <span className="eq-th-line">takes</span>
                    </button>{sort.key === "share" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="share" onMouseDown={hand.onMouseDown} /></th>
                    <th className="num">Originator<ColResize id="origShare" onMouseDown={hand.onMouseDown} /></th>
                    <th className="num"><button type="button" className="eq-sortbtn" onClick={() => toggleSort("toRunner")}>To runner</button>{sort.key === "toRunner" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="toRunner" onMouseDown={hand.onMouseDown} /></th>
                    <th className="num"><button type="button" className="eq-sortbtn" onClick={() => toggleSort("toOrig")}>To originator</button>{sort.key === "toOrig" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="toOrig" onMouseDown={hand.onMouseDown} /></th>
                    <th><button type="button" className="eq-sortbtn" onClick={() => toggleSort("from")}>Set by</button>{sort.key === "from" && <span className="eq-sort">{sort.dir === "asc" ? "\u2191" : "\u2193"}</span>}<ColResize id="from" onMouseDown={hand.onMouseDown} /></th>
                  </tr>
                </thead>
                <tbody>
                  {shownHandovers.map((h) => (
                    <tr key={h.key}>
                      <td title={h.project}>{h.name || h.project}</td>
                      <td>{h.client || <span className="muted">—</span>}</td>
                      <td className="num muted">{h.year}</td>
                      <td>{label(h.ran)}</td>
                      <td>{eligibleSet.has(h.orig) ? label(h.orig)
                            : <span className="muted">{h.orig.toUpperCase()} · outside</span>}</td>
                      <td className="num">{money0(h.kept)}</td>
                      <td className="num">
                        <input type="number" className="eq-num" min="0" max="100" step="1"
                               aria-label={`Runner share for ${h.project}`}
                               placeholder={h.share.toFixed(0)}
                               value={splits[h.key] ?? ""}
                               onChange={(e) => setSplit(h, e.target.value)} />
                      </td>
                      <td className="num muted">{(100 - h.share).toFixed(0)}p</td>
                      <td className="num">{money0(h.toRunner)}</td>
                      <td className="num">{money0(h.toOrig)}</td>
                      <td className="muted">
                        {h.from === "project" ? "this project"
                          : h.from === "partner" ? "partner rate"
                          : h.from === "outside" ? `CC / OLD (${ccShare}p)`
                          : h.from === "notpartner" ? "runner not a partner"
                          : h.share >= threshold ? `capped at ${threshold}p`
                          : "genesis %"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card title="Equity by year"
              note="the same split struck on one year at a time — context, not what the headline is built from"
              flush>
          <div className="tbl-wrap tbl-wrap--flush">
            <table className="tbl tbl-zebra">
              <thead>
                <tr>
                  <th className="sticky-col eq-name">Partner</th>
                  {YEARS.map((y) => (
                    <th key={y} className="num">{y}{!yearW[y] && <span className="muted"> (off)</span>}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scored.map((p) => (
                  <tr key={p.partner}>
                    <td className="sticky-col eq-name">{label(p.partner)}</td>
                    {YEARS.map((y) => {
                      const v = perYear[y]?.[p.partner] || 0;
                      return (
                        <td key={y} className={`num${yearW[y] ? "" : " muted"}`}>
                          {v ? `${v.toFixed(2)}%` : <span className="muted">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="eq-asides">
          {!!notTicked.length && (
            <Card title="Not included" note="click to bring into the split">
              <ul className="eq-chips">
                {notTicked.map((p) => {
                  const brought = YEARS.reduce(
                    (t, y) => t + (p.years[y]?.ro || 0) + (p.years[y]?.rg || 0), 0);
                  return (
                    <li key={p.partner}>
                      <button type="button" className="eq-chip"
                              onClick={() => toggle(p.partner)}>
                        {label(p.partner)}
                        <span className="eq-chip-sub">{moneyShort(brought)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}

          {!!data?.ignored?.length && (
            <Card title="Not credited to anyone"
                  note="genesis codes that are not a login">
              <ul className="eq-chips">
                {data.ignored.map((g) => (
                  <li key={g.code}>
                    <span className="eq-chip eq-chip--static">
                      {g.code}
                      <span className="eq-chip-sub">{moneyShort(num(g.revenue))}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="toolbar-note">
                Their share of the profit goes to <em>Others</em>. Map one to a login and it
                joins the split.
              </p>
            </Card>
          )}
        </div>

        <p className="toolbar-note">
          Costs exclude partner drawings, and are pushed onto originators pro-rata by revenue
          within each team and year — the ledger records them against a team, never a person.
          Project splits are saved for everyone; the settings on the top bar are remembered in
          this browser only.
        </p>
        </>
        )}
      </div>
    </main>
  );
}
